import React, { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import Spinner from '@cloudscape-design/components/spinner';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import Badge from '@cloudscape-design/components/badge';
import { PaymentDocument, BeaconIngestionEvent } from '../@types/types';
import { STATUS_ORDER } from '../constants';
import '../css/payment.css';



const formatTs = (ts?: string | number | null) => {
  if (!ts) return '-';
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
};

const formatLatency = (ms?: number | null) => {
  if (ms == null) return null;
  if (ms >= 60000) return `${(ms / 60000).toFixed(1)} min`;
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} sec`;
  return `${ms} ms`;
};

const stateColor = (state: string): 'success' | 'error' | 'in-progress' | 'info' => {
  if (state === 'SUCCESS') return 'success';
  if (state === 'FAILED') return 'error';
  if (state === 'IN_PROGRESS') return 'in-progress';
  return 'info';
};

const dotClass = (hasError: boolean, isLast: boolean) =>
  `timeline__dot ${hasError ? 'timeline__dot--error' : isLast ? 'timeline__dot--success' : 'timeline__dot--progress'}`;

const PaymentLookup: React.FC<{ token: string; initialId?: string; apiBase?: string }> = ({ token, initialId, apiBase }) => {
  const [endToEndId, setEndToEndId] = useState(initialId || '');
  const [payment, setPayment] = useState<PaymentDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (id?: string) => {
    const searchId = (id || endToEndId).trim();
    if (!searchId) return;
    setLoading(true);
    setError(null);
    setPayment(null);
    try {
      const res = await fetch(`${apiBase}/payment/${encodeURIComponent(searchId)}`, {
        headers: { Authorization: token },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setError(body?.message || `API error: ${res.status}`);
        return;
      }
      const data = await res.json();
      setPayment(data.payment);
    } catch (e: any) {
      setError(e.message || 'Failed to fetch payment');
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  React.useEffect(() => {
    if (initialId && !searched) handleSearch(initialId);
  }, [initialId]);

  const sortedStatuses = payment?.statusMap
    ? Object.keys(payment.statusMap).sort(
        (a, b) => (STATUS_ORDER.indexOf(a) === -1 ? 99 : STATUS_ORDER.indexOf(a))
               - (STATUS_ORDER.indexOf(b) === -1 ? 99 : STATUS_ORDER.indexOf(b))
      )
    : [];

  const totalE2E = sortedStatuses.reduce((sum, s) => sum + (payment?.statusMap?.[s]?.latencyMs || 0), 0);

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Payment Lookup</Header>}>
        <SpaceBetween size="m" direction="horizontal" alignItems="end">
          <FormField label="End-to-End ID" stretch>
            <Input
              value={endToEndId}
              onChange={({ detail }) => setEndToEndId(detail.value)}
              placeholder="e.g. DASAVFAR7YBWZBC"
              onKeyDown={({ detail }) => { if (detail.key === 'Enter') handleSearch(); }}
            />
          </FormField>
          <Button variant="primary" onClick={() => handleSearch()} loading={loading}>Search</Button>
        </SpaceBetween>
      </Container>

      {error && <Alert type="error">{error}</Alert>}
      {loading && <Box textAlign="center"><Spinner size="large" /></Box>}

      {payment && (
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Payment Details</Header>}>
            <KeyValuePairs
              columns={3}
              items={[
                { label: 'End-to-End ID', value: payment.id || '-' },
                { label: 'Payment Type', value: <Badge color="blue">{payment.paymentType}</Badge> },
                { label: 'Status', value: <StatusIndicator type={payment.beaconPaymentState === 'SUCCESS' ? 'success' : payment.beaconPaymentState === 'FAILED' ? 'error' : 'in-progress'}>{payment.beaconPaymentStatus}</StatusIndicator> },
                { label: 'State', value: <Badge color={payment.beaconPaymentState === 'SUCCESS' ? 'green' : payment.beaconPaymentState === 'FAILED' ? 'red' : 'blue'}>{payment.beaconPaymentState}</Badge> },
                { label: 'Created', value: formatTs(payment.createdAt) },
                { label: 'Updated', value: formatTs(payment.updatedAt) },
              ]}
            />
          </Container>

          {sortedStatuses.length > 0 && (
            <Container header={<Header variant="h2">Total E2E: {formatLatency(totalE2E) || '-'}</Header>}>
              <div className="timeline">
                {sortedStatuses.map((status, i) => {
                  const detail = payment.statusMap![status]!;
                  const isLast = i === sortedStatuses.length - 1;
                  const states = detail.stateMap ? Object.keys(detail.stateMap) : [];
                  const hasError = states.includes('FAILED');

                  const events: { state: string; evt: BeaconIngestionEvent }[] = [];
                  if (detail.stateMap) {
                    Object.entries(detail.stateMap).forEach(([state, evts]) =>
                      evts.forEach(evt => events.push({ state, evt }))
                    );
                  }

                  return (
                    <div key={status} className={`timeline__item ${isLast ? 'timeline__item--last' : ''}`}>
                      {!isLast && <div className="timeline__line" />}
                      <div className={dotClass(hasError, isLast)} />
                      <ExpandableSection
                        headerText={
                          <span className="timeline__header">
                            <Badge color={hasError ? 'red' : isLast ? 'green' : 'blue'}>{status}</Badge>
                            <span className="timeline__timestamp">{formatTs(detail.enteredAt)}</span>
                            {states.map(s => (
                              <StatusIndicator key={s} type={stateColor(s)}>{s}</StatusIndicator>
                            ))}
                          </span>
                        }
                      >
                        <SpaceBetween size="s">
                          <KeyValuePairs columns={1} items={[
                            { label: 'Entered At', value: formatTs(detail.enteredAt) },
                          ]} />
                          {events.length > 0 && (
                            <Table
                              columnDefinitions={[
                                { id: 'state', header: 'State', cell: (item: { state: string; evt: BeaconIngestionEvent }) => <StatusIndicator type={stateColor(item.state)}>{item.state}</StatusIndicator> },
                                { id: 'eventType', header: 'Event Type', cell: (item: { state: string; evt: BeaconIngestionEvent }) => item.evt.eventType || '-' },
                                { id: 'messageType', header: 'Message Type', cell: (item: { state: string; evt: BeaconIngestionEvent }) => item.evt.messageType || '-' },
                                { id: 'paymentStatus', header: 'Payment Status', cell: (item: { state: string; evt: BeaconIngestionEvent }) => item.evt.paymentStatus || '-' },
                                { id: 'responseType', header: 'Response Type', cell: (item: { state: string; evt: BeaconIngestionEvent }) => item.evt.responseType || '-' },
                                { id: 'responseStatus', header: 'Response Status', cell: (item: { state: string; evt: BeaconIngestionEvent }) => item.evt.responseStatus || '-' },
                              ]}
                              items={events}
                              variant="embedded"
                              sortingDisabled
                            />
                          )}
                        </SpaceBetween>
                      </ExpandableSection>
                    </div>
                  );
                })}
              </div>
            </Container>
          )}

          {payment.transformationDetails && (
            <Container header={<Header variant="h2">Transformation</Header>}>
              <KeyValuePairs columns={3} items={[
                { label: 'Time', value: formatTs(payment.transformationDetails.transformationTime) },
                { label: 'Batch Index', value: payment.transformationDetails.batchIndex || '-' },
                { label: 'File Name', value: payment.transformationDetails.fileName || '-' },
                { label: 'Plugin', value: payment.transformationDetails.pluginName || '-' },
                { label: 'Error', value: payment.transformationDetails.errorDetails || '-' },
              ].filter(i => i.value !== '-')} />
            </Container>
          )}

          {payment.transmissionDetails && (
            <Container header={<Header variant="h2">Transmission</Header>}>
              <KeyValuePairs columns={3} items={[
                { label: 'Time', value: formatTs(payment.transmissionDetails.transmissionTime) },
                { label: 'Connector', value: payment.transmissionDetails.connectorId || '-' },
                { label: 'File Name', value: payment.transmissionDetails.fileName || '-' },
                { label: 'Execution ID', value: payment.transmissionDetails.executionId || '-' },
                { label: 'Execution Status', value: payment.transmissionDetails.executionStatus
                  ? <StatusIndicator type={payment.transmissionDetails.executionStatus === 'SUCCEEDED' ? 'success' : 'error'}>{payment.transmissionDetails.executionStatus}</StatusIndicator>
                  : '-' },
                { label: 'Error', value: payment.transmissionDetails.errorDetails || '-' },
              ].filter(i => i.value !== '-')} />
            </Container>
          )}

          {payment.bankResponses && Object.keys(payment.bankResponses).length > 0 && (
            <Container header={<Header variant="h2" counter={`(${Object.keys(payment.bankResponses).length})`}>Bank Responses</Header>}>
              <Table
                columnDefinitions={[
                  { id: 'type', header: 'Type', cell: (item: [string, any]) => <Badge>{item[0]}</Badge> },
                  { id: 'status', header: 'Status', cell: (item: [string, any]) => {
                    const s = item[1].status;
                    if (!s) return '-';
                    return <Badge color={['ACCP','ACCEPTED','APPROVED'].includes(s) ? 'green' : 'red'}>{s}</Badge>;
                  }},
                  { id: 'received', header: 'Received', cell: (item: [string, any]) => formatTs(item[1].receivedAt) },
                  { id: 'file', header: 'File', cell: (item: [string, any]) => item[1].fileName || '-' },
                  { id: 'path', header: 'Path', cell: (item: [string, any]) => item[1].filePath || '-' },
                ]}
                items={Object.entries(payment.bankResponses)}
                variant="embedded"
                sortingDisabled
              />
            </Container>
          )}
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
};

export default PaymentLookup;
